import React, { useState } from 'react';

import Container from 'components/Container';
import Button from 'components/Button';
import ContactForm from 'components/ContactForm';
import WhatsAppChat from 'components/WhatsAppChat';
import supermanImage from 'assets/images/superman_rwt_no_background.png';

import {
  PageWrapper,
  HeroSection,
  HeroContainer,
  HeroContent,
  HeroTitle,
  HeroSubtitle,
  HeroAnimationWrapper,
  ProblemSolutionSection,
  SectionContainer,
  SectionTitle,
  ProblemsGrid,
  ProblemCard,
  ProblemIcon,
  ProblemTitle,
  ProblemText,
  SolutionText,
  UseCasesSection,
  UseCasesTitle,
  UseCasesGrid,
  UseCaseCard,
  UseCaseHeader,
  UseCaseIcon,
  UseCaseTitle,
  UseCaseExample,
  UseCaseQuestion,
  HowItWorksSection,
  StepsContainer,
  StepCard,
  StepNumber,
  StepTitle,
  StepDescription,
  PricingSection,
  PricingCard,
  PricingTitle,
  PriceTag,
  MonthlyPrice,
  PriceSubtext,
  FeaturesList,
  FeatureItem,
  CTAButton,
  FAQSection,
  FAQContainer,
  FAQItem,
  FAQQuestion,
  FAQAnswer,
  SupermanImage,
  SupermanWrapper,
} from './SuperAgent.styles';

const SuperAgent = () => {
  const [isContactOpen, setIsContactOpen] = useState(false);

  const problems = [
    {
      icon: (
        <svg viewBox="0 0 24 24" width="28" height="28" fill="none">
          <rect
            x="5"
            y="2"
            width="14"
            height="20"
            rx="2"
            stroke="#25d366"
            strokeWidth="2"
          />
          <line
            x1="10"
            y1="19"
            x2="14"
            y2="19"
            stroke="#25d366"
            strokeWidth="2"
            strokeLinecap="round"
          />
          <path
            d="M9 7h6M9 10h4"
            stroke="#25d366"
            strokeWidth="1.5"
            strokeLinecap="round"
          />
        </svg>
      ),
      title: 'Customers Expect Instant Answers?',
      problem:
        'Clients message at all hours asking about availability, prices, and orders — and every slow reply is a lost sale.',
      solution:
        'Your AI agent replies instantly, 24/7 — on WhatsApp, Instagram, and Facebook',
    },
    {
      icon: (
        <svg viewBox="0 0 24 24" width="28" height="28" fill="none">
          <circle cx="12" cy="12" r="10" stroke="#25d366" strokeWidth="2" />
          <path
            d="M8 8h2a2 2 0 0 1 2 2v0a2 2 0 0 1-2 2h-1"
            stroke="#25d366"
            strokeWidth="2"
            strokeLinecap="round"
          />
          <path
            d="M14 8h2a2 2 0 0 1 2 2v0a2 2 0 0 1-2 2h-1"
            stroke="#25d366"
            strokeWidth="2"
            strokeLinecap="round"
          />
          <path
            d="M9 15h6"
            stroke="#25d366"
            strokeWidth="2"
            strokeLinecap="round"
            strokeDasharray="2 2"
          />
        </svg>
      ),
      title: 'Operations Scattered Everywhere?',
      problem:
        'Inventory in one app, appointments in another, invoices in a folder — nothing talks to each other.',
      solution: 'One inbox, one agent — all channels centralized',
    },
    {
      icon: (
        <svg viewBox="0 0 24 24" width="28" height="28" fill="none">
          <path
            d="M4 4h16v16H4z"
            stroke="#25d366"
            strokeWidth="2"
            strokeLinejoin="round"
          />
          <path d="M4 9h16" stroke="#25d366" strokeWidth="2" />
          <path
            d="M8 13h4M8 16h6"
            stroke="#25d366"
            strokeWidth="1.5"
            strokeLinecap="round"
          />
        </svg>
      ),
      title: 'Manual Tasks Eating Your Day?',
      problem:
        'Generating invoices, confirming bookings, checking stock levels — repetitive tasks steal hours from growing your business.',
      solution: 'Automate the busywork, focus on what matters',
    },
    {
      icon: (
        <svg viewBox="0 0 24 24" width="28" height="28" fill="none">
          <rect
            x="3"
            y="3"
            width="18"
            height="18"
            rx="2"
            stroke="#25d366"
            strokeWidth="2"
          />
          <line
            x1="7"
            y1="16"
            x2="7"
            y2="11"
            stroke="#25d366"
            strokeWidth="2"
            strokeLinecap="round"
          />
          <line
            x1="12"
            y1="16"
            x2="12"
            y2="8"
            stroke="#25d366"
            strokeWidth="2"
            strokeLinecap="round"
          />
          <line
            x1="17"
            y1="16"
            x2="17"
            y2="13"
            stroke="#25d366"
            strokeWidth="2"
            strokeLinecap="round"
          />
        </svg>
      ),
      title: 'No Visibility Into Your Business?',
      problem:
        "You can't quickly check daily sales, low stock alerts, or upcoming appointments without digging through spreadsheets.",
      solution: 'Ask a question, get a real-time dashboard',
    },
  ];

  const useCases = [
    {
      icon: (
        <svg viewBox="0 0 24 24" width="26" height="26" fill="none">
          <path
            d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z"
            stroke="#25d366"
            strokeWidth="2"
            strokeLinejoin="round"
          />
          <line x1="3" y1="6" x2="21" y2="6" stroke="#25d366" strokeWidth="2" />
          <path
            d="M16 10a4 4 0 0 1-8 0"
            stroke="#25d366"
            strokeWidth="2"
            strokeLinecap="round"
          />
        </svg>
      ),
      title: 'Retail & Inventory',
      examples: [
        'How many units of milk do we have in stock?',
        "Show me today's sales summary and top-selling products",
        'Place an order for a customer and generate the invoice automatically',
        'Alert me when any product falls below minimum stock levels',
      ],
    },
    {
      icon: (
        <svg viewBox="0 0 24 24" width="26" height="26" fill="none">
          <rect
            x="3"
            y="4"
            width="18"
            height="18"
            rx="2"
            stroke="#25d366"
            strokeWidth="2"
          />
          <line
            x1="3"
            y1="10"
            x2="21"
            y2="10"
            stroke="#25d366"
            strokeWidth="2"
          />
          <line
            x1="16"
            y1="2"
            x2="16"
            y2="6"
            stroke="#25d366"
            strokeWidth="2"
            strokeLinecap="round"
          />
          <line
            x1="8"
            y1="2"
            x2="8"
            y2="6"
            stroke="#25d366"
            strokeWidth="2"
            strokeLinecap="round"
          />
          <circle cx="12" cy="16" r="2" fill="#25d366" />
        </svg>
      ),
      title: 'Appointments & Services',
      examples: [
        'Is there availability this afternoon with any stylist?',
        'Book a haircut + beard trim for 3 PM and confirm via WhatsApp',
        'Reschedule my 10 AM appointment to tomorrow at the same time',
        'What services do you offer and how long does each one take?',
      ],
    },
    {
      icon: (
        <svg viewBox="0 0 24 24" width="26" height="26" fill="none">
          <path
            d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"
            stroke="#25d366"
            strokeWidth="2"
            strokeLinejoin="round"
          />
          <polyline
            points="9 22 9 12 15 12 15 22"
            stroke="#25d366"
            strokeWidth="2"
            strokeLinejoin="round"
          />
        </svg>
      ),
      title: 'Real Estate & Analysis',
      examples: [
        'Analyze this property listing and give me a full market report',
        'Compare Property #1 vs Property #2 — which is the better deal?',
        'What are the neighborhood stats, average income, and nearby amenities?',
        'Estimate my monthly mortgage and suggest an offer range',
      ],
    },
    {
      icon: (
        <svg viewBox="0 0 24 24" width="26" height="26" fill="none">
          <path
            d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"
            stroke="#25d366"
            strokeWidth="2"
            strokeLinejoin="round"
          />
          <circle cx="9" cy="12" r="1" fill="#25d366" />
          <circle cx="12" cy="12" r="1" fill="#25d366" />
          <circle cx="15" cy="12" r="1" fill="#25d366" />
        </svg>
      ),
      title: 'Social Media & Messaging',
      examples: [
        'Reply to all Instagram DMs asking about product availability',
        'Auto-respond to Facebook messages after business hours',
        'Centralize all incoming messages from WhatsApp, IG, and FB into one dashboard',
        "Send automated follow-ups to customers who inquired but didn't purchase",
      ],
    },
  ];

  const steps = [
    {
      number: 1,
      title: 'We Set It Up',
      description:
        'Our team installs and configures everything in 1-2 days. No technical skills needed from you.',
    },
    {
      number: 2,
      title: 'Connect Your Tools',
      description:
        'We securely link your email, calendar, contacts, Google Sheets, and Google Docs to the system.',
    },
    {
      number: 3,
      title: 'Go Live on All Channels',
      description:
        'Your agent starts handling conversations on WhatsApp, Instagram, and Facebook. Train your team in minutes.',
    },
  ];

  const features = [
    'Unlimited conversations across WhatsApp, Instagram & Facebook',
    'Centralized inbox for all channels',
    'Inventory & order management',
    'Appointment booking & scheduling',
    'Automated invoicing & payment verification',
    'Real-time dashboards & analytics',
    'Multi-language support',
    'Custom integrations for your business',
    'Enterprise-grade security',
  ];

  const faqs = [
    {
      question: 'Do I need technical skills to use this?',
      answer:
        'Not at all. If you can use WhatsApp, Instagram, or Facebook, you can use Super Agent. We handle all the technical setup, channel connections, and configuration for you.',
    },
    {
      question: 'How customizable is the agent for my business?',
      answer:
        'Every Super Agent is tailored to your specific operations — your products, services, schedules, and workflows. It learns your business and speaks your language.',
    },
    {
      question: 'Can my employees use it too?',
      answer:
        'Yes! Your team can interact with the agent through a centralized multi-channel dashboard with different permission levels, and you get a management view to oversee everything.',
    },
    {
      question: 'Which messaging platforms do you support?',
      answer:
        'We currently support WhatsApp, Instagram DMs, and Facebook Messenger — with more channels on the roadmap.',
    },
    {
      question: 'Is my business data secure?',
      answer:
        'Absolutely. We use enterprise-grade security with prompt injection detection and encrypted connections. Your data stays private and isolated.',
    },
    {
      question: 'What kind of businesses is this for?',
      answer:
        'Any business that handles inventory, appointments, customer orders, or needs social media automation and real-time data access. From retail stores and salons to real estate agencies and service providers.',
    },
    {
      question: 'How long does setup take?',
      answer:
        'It depends on the complexity and specific needs of your agent, but most businesses are up and running within 1-2 weeks. We handle the integration, customization, and training so you can focus on your business.',
    },
  ];

  return (
    <>
      <PageWrapper>
        <HeroSection>
          <HeroContainer>
            <HeroContent>
              <HeroTitle>One AI Agent for All Your Messaging</HeroTitle>
              <HeroSubtitle>
                Handle customer conversations across WhatsApp, Instagram, and
                Facebook from one place. Automate replies, manage orders, and
                book appointments — without switching between apps.
              </HeroSubtitle>
              <Button
                onClick={() => setIsContactOpen(true)}
                styles={`
                  padding: 18px 40px;
                  background: #1a1a1a;
                  color: #25d366;
                  font-weight: 700;
                  border-radius: 10px;
                  font-size: 18px;
                  transition: all 0.2s ease;
                  border: 2px solid #25d366;

                  &:hover {
                    background: transparent;
                    color: #1a1a1a;
                    border-color: #25d366;
                  }
                `}
              >
                Get a Custom Quote
              </Button>
            </HeroContent>
            <HeroAnimationWrapper>
              <WhatsAppChat />
            </HeroAnimationWrapper>
          </HeroContainer>
        </HeroSection>

        <ProblemSolutionSection>
          <SectionContainer>
            <SectionTitle>Say Goodbye to Daily Headaches</SectionTitle>
            <ProblemsGrid>
              {problems.map((item) => (
                <ProblemCard key={item.title}>
                  <ProblemIcon>{item.icon}</ProblemIcon>
                  <div>
                    <ProblemTitle>{item.title}</ProblemTitle>
                    <ProblemText>{item.problem}</ProblemText>
                  </div>
                  <SolutionText>{item.solution}</SolutionText>
                </ProblemCard>
              ))}
            </ProblemsGrid>
          </SectionContainer>
        </ProblemSolutionSection>

        <UseCasesSection>
          <Container>
            <UseCasesTitle>Real Questions, Real Answers</UseCasesTitle>
            <UseCasesGrid>
              {useCases.map((useCase) => (
                <UseCaseCard key={useCase.title}>
                  <UseCaseHeader>
                    <UseCaseIcon>{useCase.icon}</UseCaseIcon>
                    <UseCaseTitle>{useCase.title}</UseCaseTitle>
                  </UseCaseHeader>
                  {useCase.examples.map((example) => (
                    <UseCaseExample key={example}>
                      <UseCaseQuestion>{example}</UseCaseQuestion>
                    </UseCaseExample>
                  ))}
                </UseCaseCard>
              ))}
            </UseCasesGrid>
          </Container>
        </UseCasesSection>

        <HowItWorksSection>
          <SectionContainer>
            <SectionTitle>How It Works</SectionTitle>
            <StepsContainer>
              {steps.map((step) => (
                <StepCard key={step.number}>
                  <StepNumber>{step.number}</StepNumber>
                  <StepTitle>{step.title}</StepTitle>
                  <StepDescription>{step.description}</StepDescription>
                </StepCard>
              ))}
            </StepsContainer>
          </SectionContainer>
        </HowItWorksSection>

        <PricingSection>
          <PricingTitle>Ready to Transform Your Business?</PricingTitle>
          <PricingCard>
            <SupermanWrapper>
              <SupermanImage src={supermanImage} alt="Super Agent RWT" />
            </SupermanWrapper>
            <PriceTag>
              <MonthlyPrice style={{ fontSize: '2rem', color: '#1f2937' }}>
                Every business is unique
              </MonthlyPrice>
              <PriceSubtext>
                We tailor each Super Agent to your specific needs, integrations,
                and workflows. Get in touch and we&apos;ll build a plan that
                fits.
              </PriceSubtext>
            </PriceTag>
            <FeaturesList>
              {features.map((feature) => (
                <FeatureItem key={feature}>{feature}</FeatureItem>
              ))}
            </FeaturesList>
            <CTAButton onClick={() => setIsContactOpen(true)}>
              Contact Us for a Quote
            </CTAButton>
          </PricingCard>
        </PricingSection>

        <FAQSection>
          <SectionContainer>
            <SectionTitle>Frequently Asked Questions</SectionTitle>
            <FAQContainer>
              {faqs.map((faq) => (
                <FAQItem key={faq.question}>
                  <FAQQuestion>{faq.question}</FAQQuestion>
                  <FAQAnswer>{faq.answer}</FAQAnswer>
                </FAQItem>
              ))}
            </FAQContainer>
          </SectionContainer>
        </FAQSection>
      </PageWrapper>
      <ContactForm
        isOpen={isContactOpen}
        onClose={() => setIsContactOpen(false)}
      />
    </>
  );
};

export default SuperAgent;
